/**
 * Team Columns View — Smart Operational Scheduling Platform™
 *
 * Premium calendar view where each staff member gets their own column.
 * Drag & drop between staff/time. Smooth, enterprise-grade UX.
 *
 * Props:
 *   appointments: [{appointment_id, staff_id, service_name, client_name,
 *                   start_time | time, duration | total_duration,
 *                   date (yyyy-MM-dd), status, is_blocked}]
 *   staff:        [{staff_id, name, color, role, service_ids, photo_url}]
 *   services:     [{service_id, name}]  (optional, used for filter + warnings)
 *   currentDate:  Date
 *   onMoveAppointment(appt, newStaffId, newHourMinute) → Promise
 *   onClickAppointment(appt)
 *   onClickSlot(staff, hour)
 *   workingHours: { start: "08:00", end: "20:00", slotMinutes: 30 }
 *   filterServiceId: string|null
 *   onFilterServiceChange(serviceId|null)
 */
import React, { useMemo, useState, useCallback } from "react";
import { format, parseISO, isSameDay } from "date-fns";
import { it as itLocale, enUS } from "date-fns/locale";
import {
  Plus, AlertCircle, Users, ArrowLeftRight, Filter, X, AlertTriangle, Briefcase,
} from "lucide-react";

const STATUS_TONE = {
  scheduled: "from-blue-500 to-cyan-500",
  confirmed: "from-emerald-500 to-teal-500",
  pending:   "from-amber-500 to-orange-500",
  awaiting_confirmation: "from-orange-500 to-amber-500",
  completed: "from-violet-500 to-fuchsia-500",
  cancelled: "from-rose-500 to-red-500",
  no_show:   "from-slate-500 to-slate-700",
};

const HOUR_HEIGHT_PX     = 52;
const SLOT_HEIGHT_PX     = 26; // 30 min slot
const STAFF_COLUMN_MIN_W = 220;
const TIME_GUTTER_W      = 64;

function buildHourLabels(start = "08:00", end = "20:00") {
  const [sh] = start.split(":").map(Number);
  const [eh] = end.split(":").map(Number);
  const out = [];
  for (let h = sh; h <= eh; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
  }
  return out;
}

/** Compute Y offset (px) for a HH:MM time relative to first hour displayed */
function timeToY(time, firstHour) {
  if (!time) return 0;
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h)) return 0;
  return (h - firstHour) * HOUR_HEIGHT_PX + (m / 60) * HOUR_HEIGHT_PX;
}

/** Quantize a Y px back to "HH:MM" (snap to 15min) */
function yToTime(y, firstHour) {
  const totalMinutes = Math.round((y / HOUR_HEIGHT_PX) * 60 / 15) * 15;
  const h = firstHour + Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Normalize an appointment to {start, duration, end} (minutes) for overlap math */
function apptToMinutes(a) {
  const t = a.start_time || a.time;
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  const start  = h * 60 + (m || 0);
  const dur    = parseInt(a.total_duration || a.duration || 30, 10);
  return { start, dur, end: start + dur };
}

/** Mark overlap groups within a single staff column */
function annotateOverlaps(items) {
  // items pre-sorted by start
  const out = items.map((it) => ({ ...it, _conflict: false }));
  for (let i = 0; i < out.length; i++) {
    for (let j = i + 1; j < out.length; j++) {
      const a = apptToMinutes(out[i]);
      const b = apptToMinutes(out[j]);
      if (!a || !b) continue;
      if (a.end > b.start && b.end > a.start) {
        out[i]._conflict = true;
        out[j]._conflict = true;
      }
    }
  }
  return out;
}

const TeamColumnsView = ({
  appointments = [],
  staff = [],
  services = [],
  currentDate = new Date(),
  onMoveAppointment,
  onClickAppointment,
  onClickSlot,
  workingHours = { start: "08:00", end: "20:00", slotMinutes: 30 },
  language = "it",
  emptyHint,
  filterServiceId = null,
  onFilterServiceChange,
}) => {
  const hours = useMemo(() => buildHourLabels(workingHours.start, workingHours.end),
    [workingHours.start, workingHours.end]);
  const firstHour = parseInt(workingHours.start.split(":")[0], 10);

  const [dragId,    setDragId]    = useState(null);
  const [hoverDrop, setHoverDrop] = useState(null);  // {staff_id, time}

  const filterService = useMemo(
    () => services.find(s => s.service_id === filterServiceId),
    [services, filterServiceId]
  );

  // Filter appointments to currentDate
  const dayAppts = useMemo(() => {
    const target = format(currentDate, "yyyy-MM-dd");
    return (appointments || []).filter(a => (a.date || "").slice(0, 10) === target);
  }, [appointments, currentDate]);

  // Staff visible (apply service filter if any) — but always show ALL staff
  // even when filter is applied, so admin can drag onto "not eligible" columns
  // intentionally; we visually mark those as dimmed.
  const visibleStaff = staff;

  // Group by staff (with overlap annotation)
  const byStaff = useMemo(() => {
    const m = {};
    staff.forEach(s => { m[s.staff_id] = []; });
    dayAppts.forEach(a => {
      if (!m[a.staff_id]) m[a.staff_id] = [];
      m[a.staff_id].push(a);
    });
    // sort + annotate
    Object.keys(m).forEach(k => {
      m[k].sort((a, b) => {
        const ta = (a.start_time || a.time || "00:00");
        const tb = (b.start_time || b.time || "00:00");
        return ta.localeCompare(tb);
      });
      m[k] = annotateOverlaps(m[k]);
    });
    return m;
  }, [dayAppts, staff]);

  const tenantHasServices = services && services.length > 0;
  const anyUnconfigured = tenantHasServices && staff.some(
    s => !s.service_ids || s.service_ids.length === 0
  );

  const isEligible = (member, serviceId) => {
    if (!serviceId) return true;
    const ids = member.service_ids || [];
    if (ids.length === 0) return true; // back-compat: empty = any
    return ids.includes(serviceId);
  };

  const handleDragStart = (e, appt) => {
    if (appt.is_blocked || appt.status === "blocked") {
      e.preventDefault();
      return;
    }
    setDragId(appt.appointment_id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", appt.appointment_id); } catch { /* noop */ }
    // transparent ghost
    try {
      const img = document.createElement("div");
      img.style.cssText = "position:absolute;top:-9999px;width:1px;height:1px;";
      document.body.appendChild(img);
      e.dataTransfer.setDragImage(img, 0, 0);
      setTimeout(() => document.body.removeChild(img), 0);
    } catch { /* unsupported */ }
  };

  const handleDragEnd = () => { setDragId(null); setHoverDrop(null); };

  const handleColumnDragOver = (e, staffId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    setHoverDrop({ staff_id: staffId, time });
  };

  const handleDrop = (e, staffId) => {
    e.preventDefault();
    if (!dragId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    const appt = dayAppts.find(a => a.appointment_id === dragId);
    if (appt && onMoveAppointment) {
      onMoveAppointment(appt, staffId, time);
    }
    setDragId(null);
    setHoverDrop(null);
  };

  const handleSlotClick = useCallback((e, staffObj) => {
    if (!onClickSlot) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const time = yToTime(Math.max(0, y), firstHour);
    onClickSlot(staffObj, time);
  }, [onClickSlot, firstHour]);

  // === Render ===
  if (staff.length === 0) {
    return (
      <div className="rounded-3xl border-2 border-dashed border-slate-200 dark:border-white/10 p-12 text-center" data-testid="team-columns-empty">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center mb-3">
          <Users className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">
          {language === "it" ? "Nessun membro del team" : "No team members"}
        </h3>
        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
          {emptyHint || (language === "it"
            ? "Aggiungi staff dal pannello 'Team' per attivare la vista colonne."
            : "Add staff from the 'Team' panel to enable column view.")}
        </p>
      </div>
    );
  }

  const containerHeight = hours.length * HOUR_HEIGHT_PX + 8;
  const isToday = isSameDay(currentDate, new Date());

  // "Now" marker
  const now = new Date();
  const nowH = now.getHours();
  const nowM = now.getMinutes();
  const nowY = (nowH - firstHour) * HOUR_HEIGHT_PX + (nowM / 60) * HOUR_HEIGHT_PX;
  const showNow = isToday && nowY > 0 && nowY < containerHeight;

  return (
    <div className="rounded-3xl border border-slate-200/60 dark:border-white/10 bg-white dark:bg-slate-900/40 overflow-hidden shadow-sm" data-testid="team-columns-view">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 dark:border-white/5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/60 dark:to-slate-900/30">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center">
            <Users className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Team Columns</p>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-200">
              {format(currentDate, "EEEE d MMMM yyyy", { locale: language === "it" ? itLocale : enUS })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Service filter */}
          {tenantHasServices && onFilterServiceChange && (
            <div className="flex items-center gap-1 rounded-full bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-white/10 px-2 py-1" data-testid="team-service-filter">
              <Filter className="w-3 h-3 text-slate-400" />
              <select
                className="text-[11px] font-bold bg-transparent outline-none text-slate-700 dark:text-slate-200 pr-1 max-w-[180px]"
                value={filterServiceId || ""}
                onChange={(e) => onFilterServiceChange(e.target.value || null)}
                data-testid="team-service-filter-select"
              >
                <option value="">{language === "it" ? "Tutti i servizi" : "All services"}</option>
                {services.map(s => (
                  <option key={s.service_id} value={s.service_id}>{s.name}</option>
                ))}
              </select>
              {filterService && (
                <button
                  type="button"
                  onClick={() => onFilterServiceChange(null)}
                  className="text-slate-400 hover:text-slate-600"
                  title={language === "it" ? "Cancella filtro" : "Clear filter"}
                  data-testid="team-service-filter-clear"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          <div className="hidden sm:flex items-center gap-1 text-[10px] text-slate-400">
            <ArrowLeftRight className="w-3 h-3" />
            {language === "it" ? "Trascina per riassegnare" : "Drag to reassign"}
          </div>
        </div>
      </header>

      {/* Configuration nudge */}
      {anyUnconfigured && (
        <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/15 border-b border-amber-200 dark:border-amber-900/40 flex items-center gap-2 text-[11px] text-amber-800 dark:text-amber-200" data-testid="team-unconfigured-banner">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span className="font-bold">
            {language === "it"
              ? "Alcuni membri del team non hanno servizi assegnati: vengono considerati abilitati a tutti i servizi."
              : "Some team members have no assigned services: they are treated as available for any service."}
          </span>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="flex" style={{ minWidth: TIME_GUTTER_W + visibleStaff.length * STAFF_COLUMN_MIN_W }}>
          {/* Time gutter */}
          <div className="shrink-0 sticky left-0 bg-white dark:bg-slate-900/40 z-10 border-r border-slate-100 dark:border-white/5"
               style={{ width: TIME_GUTTER_W }}>
            <div className="h-16 border-b border-slate-100 dark:border-white/5" />
            <div className="relative" style={{ height: containerHeight }}>
              {hours.map((h, i) => (
                <div key={h}
                     className="absolute left-0 right-0 border-t border-slate-100 dark:border-white/5 text-[10px] font-mono font-bold text-slate-400 pl-2 pt-0.5"
                     style={{ top: i * HOUR_HEIGHT_PX }}>
                  {h}
                </div>
              ))}
            </div>
          </div>

          {/* Staff columns */}
          {visibleStaff.map(member => {
            const ap          = byStaff[member.staff_id] || [];
            const accent      = member.color || "#6366f1";
            const initial     = (member.name || "?").trim().charAt(0).toUpperCase();
            const eligible    = isEligible(member, filterServiceId);
            const isUnconfigured = tenantHasServices && (!member.service_ids || member.service_ids.length === 0);
            const dim         = filterServiceId && !eligible;
            const svcCount    = (member.service_ids || []).length;
            return (
              <div key={member.staff_id} className={`shrink-0 border-r border-slate-100 dark:border-white/5 transition-opacity ${dim ? "opacity-40" : ""}`}
                   style={{ width: STAFF_COLUMN_MIN_W, minWidth: STAFF_COLUMN_MIN_W }}
                   data-testid={`team-col-${member.staff_id}`}>
                {/* Column header */}
                <div className="h-16 px-3 flex items-center gap-2 border-b border-slate-100 dark:border-white/5 sticky top-0 bg-white dark:bg-slate-900/40 z-[5]">
                  {member.photo_url ? (
                    <img src={member.photo_url} alt={member.name}
                         className="w-9 h-9 rounded-full object-cover ring-2 shrink-0"
                         style={{ boxShadow: `0 0 0 2px ${accent}` }} />
                  ) : (
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-black shrink-0"
                         style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)` }}>
                      {initial}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">{member.name}</p>
                      {isUnconfigured && (
                        <span
                          className="inline-flex items-center text-[8px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
                          title={language === "it" ? "Nessun servizio assegnato" : "No assigned services"}
                          data-testid={`team-staff-unconfigured-${member.staff_id}`}
                        >
                          <AlertCircle className="w-2.5 h-2.5 mr-0.5" />
                          {language === "it" ? "Da config." : "Config."}
                        </span>
                      )}
                    </div>
                    <p className="text-[9px] uppercase tracking-wider font-bold text-slate-400 truncate flex items-center gap-1">
                      <span className="truncate">{member.role || (language === "it" ? "Operatore" : "Staff")}</span>
                      <span>·</span>
                      <span>{ap.length} appt</span>
                      {!isUnconfigured && tenantHasServices && (
                        <>
                          <span>·</span>
                          <span className="inline-flex items-center gap-0.5">
                            <Briefcase className="w-2.5 h-2.5" /> {svcCount}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {/* Column body */}
                <div
                  className={`relative ${dragId ? "bg-blue-50/40 dark:bg-blue-900/10" : ""}`}
                  style={{ height: containerHeight }}
                  onDragOver={(e) => handleColumnDragOver(e, member.staff_id)}
                  onDrop={(e) => handleDrop(e, member.staff_id)}
                  onClick={(e) => handleSlotClick(e, member)}
                  data-testid={`team-col-body-${member.staff_id}`}
                >
                  {/* Hour grid lines */}
                  {hours.map((h, i) => (
                    <div key={h}
                         className="absolute left-0 right-0 border-t border-slate-100 dark:border-white/5"
                         style={{ top: i * HOUR_HEIGHT_PX }} />
                  ))}
                  {/* Half-hour ticks */}
                  {hours.map((h, i) => (
                    <div key={`half-${h}`}
                         className="absolute left-0 right-0 border-t border-dashed border-slate-100/70 dark:border-white/[0.04]"
                         style={{ top: i * HOUR_HEIGHT_PX + SLOT_HEIGHT_PX }} />
                  ))}

                  {/* Now line */}
                  {showNow && (
                    <div className="absolute left-0 right-0 z-[3] pointer-events-none" style={{ top: nowY }}>
                      <div className="h-px bg-gradient-to-r from-rose-500 to-pink-500" />
                      <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full bg-rose-500 shadow" />
                    </div>
                  )}

                  {/* Drop ghost */}
                  {hoverDrop?.staff_id === member.staff_id && dragId && (
                    <div className="absolute left-1.5 right-1.5 rounded-xl border-2 border-dashed border-blue-400 bg-blue-50/70 dark:bg-blue-900/30 pointer-events-none transition-all"
                         style={{ top: timeToY(hoverDrop.time, firstHour), height: 36 }}
                         data-testid={`team-drop-ghost-${member.staff_id}`}>
                      <div className="text-[10px] font-mono font-bold text-blue-700 dark:text-blue-300 px-2 pt-1">
                        {hoverDrop.time}
                        {filterServiceId && !eligible && (
                          <span className="ml-2 text-rose-600 dark:text-rose-300">
                            {language === "it" ? "⚠ non eligible" : "⚠ not eligible"}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Appointment cards */}
                  {ap.map(appt => {
                    const startTime = appt.start_time || appt.time;
                    const startY    = timeToY(startTime, firstHour);
                    const duration  = Math.max(15, parseInt(appt.total_duration || appt.duration || 30, 10));
                    const cardH     = Math.max(20, (duration / 60) * HOUR_HEIGHT_PX - 2);
                    const tone      = STATUS_TONE[appt.status] || STATUS_TONE.scheduled;
                    const isBlock   = appt.is_blocked || appt.kind === "block" || appt.status === "blocked";
                    const conflict  = appt._conflict;
                    return (
                      <div
                        key={appt.appointment_id}
                        draggable={!isBlock}
                        onDragStart={(e) => handleDragStart(e, appt)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => { e.stopPropagation(); if (onClickAppointment) onClickAppointment(appt); }}
                        className={`absolute left-1.5 right-1.5 rounded-xl px-2 py-1.5 cursor-pointer shadow-sm transition-all hover:shadow-md hover:-translate-y-px overflow-hidden text-white ${
                          isBlock
                            ? "bg-gradient-to-r from-slate-700 to-slate-900 opacity-90"
                            : `bg-gradient-to-r ${tone}`
                        } ${dragId === appt.appointment_id ? "opacity-50 scale-[0.98]" : ""} ${conflict ? "ring-2 ring-rose-400 ring-offset-1" : ""}`}
                        style={{ top: startY + 1, height: cardH, cursor: isBlock ? "default" : "grab" }}
                        data-testid={`team-appt-${appt.appointment_id}`}
                        title={`${appt.client_name || ""} · ${appt.service_name || ""}${conflict ? " · ⚠ sovrapposizione" : ""}`}
                      >
                        <div className="flex items-center justify-between text-[10px] font-mono font-bold opacity-95">
                          <span>{startTime}</span>
                          <span className="flex items-center gap-1">
                            {conflict && (
                              <AlertTriangle
                                className="w-3 h-3 text-amber-100"
                                aria-label={language === "it" ? "Sovrapposizione" : "Conflict"}
                              />
                            )}
                            {isBlock && <span className="uppercase tracking-wider text-[8px]">{language === "it" ? "Bloccato" : "Block"}</span>}
                          </span>
                        </div>
                        <p className="text-[11px] font-bold leading-tight truncate mt-0.5">
                          {appt.client_name || (isBlock ? (appt.reason || (language === "it" ? "Slot bloccato" : "Blocked")) : "—")}
                        </p>
                        {cardH > 28 && appt.service_name && (
                          <p className="text-[10px] opacity-90 truncate">{appt.service_name}</p>
                        )}
                      </div>
                    );
                  })}

                  {/* Empty hint when no appointments */}
                  {ap.length === 0 && !dragId && (
                    <div className="absolute inset-x-2 top-3 rounded-xl border border-dashed border-slate-200 dark:border-white/10 p-2 text-center pointer-events-none">
                      <Plus className="w-3 h-3 mx-auto text-slate-300" />
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        {language === "it" ? "Clic per nuovo appt" : "Click to add"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TeamColumnsView;
